import { useState, useCallback } from "react";
import { toast } from "sonner";
import { db } from "@/services/firebaseService";
import { useAuth } from "@/context/AuthContext";
import { 
  bulkCopyLocationData, 
  getTargetLocationsForOrg, 
  LocationSummary,
  BulkCopyResult 
} from "@/services/bulkLocationCopyService";
import { CampLocation } from "@/types/campLocation";

interface UseBulkCopyLocationProps {
  location: CampLocation;
}

interface UseBulkCopyLocationReturn {
  isModalOpen: boolean;
  isLoading: boolean;
  targetLocations: LocationSummary[];
  showBulkCopyModal: () => Promise<void>;
  closeBulkCopyModal: () => void;
  executeBulkCopy: () => Promise<void>;
}

export const useBulkCopyLocation = ({ 
  location 
}: UseBulkCopyLocationProps): UseBulkCopyLocationReturn => {
  const { currentUser, userProfile } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [targetLocations, setTargetLocations] = useState<LocationSummary[]>([]);

  /**
   * Fetches target locations and opens the confirmation modal
   */
  const showBulkCopyModal = useCallback(async () => {
    if (!currentUser || !location) {
      toast.error("Missing required data for bulk copy operation");
      return;
    }

    try {
      setIsLoading(true);
      console.log("[useBulkCopyLocation] Fetching target locations for org:", location.providedByOrg);
      
      // Get the organization ID from the location
      const orgId = typeof location.providedByOrg === 'object' 
        ? location.providedByOrg?.id 
        : location.providedByOrg;
      
      if (!orgId) {
        toast.error("Location has no organization ID");
        return;
      }

      // Fetch target locations
      const targets = await getTargetLocationsForOrg(db, orgId, location.id);
      setTargetLocations(targets);
      setIsModalOpen(true);
      
      console.log(`[useBulkCopyLocation] Found ${targets.length} target locations`);
    } catch (error) {
      console.error("[useBulkCopyLocation] Error fetching target locations:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to fetch target locations: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, location]);

  /**
   * Closes the confirmation modal and resets state
   */
  const closeBulkCopyModal = useCallback(() => {
    setIsModalOpen(false);
    setTargetLocations([]);
  }, []);

  /**
   * Executes the bulk copy operation
   */
  const executeBulkCopy = useCallback(async () => {
    if (!currentUser || !location) {
      toast.error("Missing required data for bulk copy operation");
      return;
    }

    try {
      setIsLoading(true);
      console.log("[useBulkCopyLocation] Starting bulk copy operation");
      
      const result: BulkCopyResult = await bulkCopyLocationData(
        db,
        location.id,
        currentUser.uid,
        userProfile?.role
      );

      console.log("[useBulkCopyLocation] Bulk copy completed:", result);

      // Show success/warning messages based on result
      if (result.success) {
        if (result.copiedCount === targetLocations.length) {
          toast.success(
            `Successfully copied data to ${result.copiedCount} location${result.copiedCount !== 1 ? 's' : ''}!`
          );
        } else {
          toast.warning(
            `Partially completed: ${result.copiedCount} updated, ${result.errors.length} failed`
          );
        }
      } else {
        toast.error(`Bulk copy failed: ${result.errors.join(', ')}`);
      }

      // Log any errors
      if (result.errors.length > 0) {
        console.warn("[useBulkCopyLocation] Bulk copy errors:", result.errors);
      }

      // Close modal on success
      if (result.success) {
        closeBulkCopyModal();
      }
      
    } catch (error) {
      console.error("[useBulkCopyLocation] Bulk copy operation failed:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Bulk copy failed: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, location, userProfile?.role, targetLocations.length, closeBulkCopyModal]);

  return {
    isModalOpen,
    isLoading,
    targetLocations,
    showBulkCopyModal,
    closeBulkCopyModal,
    executeBulkCopy
  };
}; 